/** @format */

import {useCallback, useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';

import {ReportsArchive} from 'ui';
import {ROUTES} from 'constants/ROUTES';

import {ReportUnavailable} from '../Report/components/ReportUnavailable';
import {useCompanyReportsQuery} from '../graphql/__generated__';
import {toArchiveData, type RawReport} from './helpers/toArchiveData';

const NBSP = '\u00A0';

/**
 * Сколько периодов показываем.
 *
 * Отчёт месячный, так что двадцать четыре строки — это два года истории.
 * Пагинации нет намеренно: кнопка «показать ещё» ради третьего года стоит
 * дороже, чем один запрос на двадцать четыре строки.
 */
const PAGE_LIMIT = 24;

/**
 * Отчёты одного проекта.
 *
 * Проект задаётся путём, а не выбранным в `CompanyContext`: ссылку на список
 * пересылают, и она обязана открывать один и тот же проект у любого, кто
 * имеет к нему доступ.
 */
const CompanyArchive = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {companyId} = useParams<{companyId: string}>();

  const {data, loading, error, refetch} = useCompanyReportsQuery({
    variables: {companyId: companyId ?? '', limit: PAGE_LIMIT},
    skip: !companyId,
  });

  const company = data?.currentUser?.company;
  const reports = data?.currentUser?.projectReports;

  const archive = useMemo(
    () => toArchiveData(company?.name, (reports ?? []) as RawReport[]),
    [company, reports],
  );

  const handleOpenReport = useCallback(
    (id: string) => navigate(`/${ROUTES.REPORT}?id=${encodeURIComponent(id)}`),
    [navigate],
  );

  const handleLogoClick = useCallback(() => navigate(ROUTES.INDEX), [navigate]);
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (!companyId) {
    return (
      <ReportUnavailable
        title={t(`Проект не${NBSP}выбран`, `Проект не${NBSP}выбран`)}
        detail={t(
          `В${NBSP}адресе страницы нет идентификатора${NBSP}проекта.`,
          `В${NBSP}адресе страницы нет идентификатора${NBSP}проекта.`,
        )}
      />
    );
  }

  // Пустой список на чужом проекте прочитался бы как «отчётов пока нет».
  // Это разные вещи, и говорить надо правду.
  if (!loading && !error && company == null) {
    return (
      <ReportUnavailable
        title={t(`Проект не${NBSP}найден`, `Проект не${NBSP}найден`)}
        detail={t(
          `Возможно, ссылка устарела или${NBSP}проект принадлежит другому${NBSP}аккаунту.`,
          `Возможно, ссылка устарела или${NBSP}проект принадлежит другому${NBSP}аккаунту.`,
        )}
      />
    );
  }

  return (
    <ReportsArchive
      data={archive}
      loading={loading && data == null}
      error={Boolean(error)}
      onRetry={handleRetry}
      onOpenReport={handleOpenReport}
      onLogoClick={handleLogoClick}
    />
  );
};

export default CompanyArchive;
